import { useEffect, useState } from "react";
import { Sparkles, Trash2, Eye, EyeOff, Loader2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";
const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", tags: [], published: true, seo_title: "", meta_description: "" };

export default function AdminBlog() {
  const [items, setItems] = useState(null);
  const [topic, setTopic] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    setGenLoading(true);
    try { await api.post("/admin/blog/generate", { topic, auto_publish: true }); toast.success("AI wrote & published a new article!"); setTopic(""); load(); }
    catch (e) { toast.error(apiError(e, "AI generation failed")); } finally { setGenLoading(false); }
  };

  const openEdit = (b) => { setForm({ ...EMPTY, ...b, tags: b.tags || [] }); setEditId(b.id); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()) : form.tags };
      if (editId) await api.put(`/admin/blog/${editId}`, payload); else await api.post("/admin/blog", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const togglePub = async (b) => { await api.put(`/admin/blog/${b.id}`, { ...b, published: !b.published }); load(); };
  const del = async (id) => { if (!window.confirm("Delete article?")) return; await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <AdminLayout title="AI Blog">
      <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-6 card-soft">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><Sparkles className="h-5 w-5 text-brandorange" /> AI Article Writer</h3>
        <p className="mt-1 text-sm text-gray-400">Leave the topic empty and AI will pick a trending travel topic, write it, and publish automatically.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="blog-topic-input" className={input} placeholder="Optional topic (e.g. Best time to visit Bali)" value={topic} onChange={(e) => setTopic(e.target.value)} />
          <button onClick={generate} disabled={genLoading} data-testid="generate-blog-btn" className="btn-gradient flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-2.5 font-bold disabled:opacity-70">{genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate & Publish</button>
          <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="flex items-center justify-center gap-2 rounded-full border-2 border-navy px-5 py-2.5 font-bold text-navy"><Plus className="h-4 w-4" /> Manual</button>
        </div>
      </div>

      {items === null ? <div className="space-y-3">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-16 skeleton rounded-2xl" />)}</div>
        : items.length === 0 ? <div className="rounded-2xl border border-dashed border-gray-200 py-16 text-center text-gray-400" data-testid="blog-admin-empty">No articles yet. Generate one above!</div>
        : (
          <div className="space-y-3">
            {items.map((b) => (
              <div key={b.id} data-testid={`blog-admin-${b.slug}`} className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 card-soft">
                <img src={b.cover_image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                <div className="flex-1"><p className="font-semibold text-navy">{b.title}</p><p className="text-xs text-gray-400">{new Date(b.created_at).toLocaleDateString("en-IN")} · {b.published ? "Published" : "Draft"}</p></div>
                <div className="flex gap-2">
                  <button onClick={() => togglePub(b)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">{b.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  <button onClick={() => openEdit(b)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(b.id)} className="rounded-lg p-2 text-brandred hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto"><DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editId ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <input className={input} placeholder="Title" value={form.title} onChange={set("title")} />
            <input className={input} placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} />
            <input className={input} placeholder="Tags (comma separated)" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={set("tags")} />
            <textarea rows={2} className={input} placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} />
            <textarea rows={10} className={input} placeholder="Content (Markdown)" value={form.content} onChange={set("content")} />
            <label className="flex items-center gap-2 text-sm font-semibold text-navy"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
            <button disabled={saving} className="btn-gradient flex w-full items-center justify-center rounded-full py-3 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
